// src/encounter/chart-column-v2.tsx
// PROD-2743 · the started-encounter CHART COLUMN on the approved design, DARK behind `?ef=frameV2`.
//
// The design Samer approved (design desk R6c `Patient Workspace.dc.html` `chartBuild`, 1 Oct 2026;
// reference capture R5-pw-physician-1440.png) and the ruled left-rail content (30 Sep): ONE row
// family for every clinical list — the shipped DS `ClinicalTermRow`, a curated short name, year +
// quiet code, a state dot, word actions on point and a "More" overflow — each section in a card
// with depth (`--color-surface-card`, 1px `--color-border-card`, `--radius-card`, `--shadow-2`,
// 8px apart), section titles in their WAYFINDING INK (map v.2), Allergies first, resolved
// problems in a collapsed `ClinicalTermGroup` drawer, and a person-first patient card with the MRN
// quiet and last.
//
// THE DATA AND THE HANDLERS ARE NOT HERE. `patient-summary-rail.tsx` builds the same interactive
// `IpsSection[]` it hands the pre-visit rail (every `onAdd`, every row action) and the same
// anchored-surface renderers (`railAddPopover` / `railActionPopover`), and passes them in. This
// file only lays them out.
//
// WHAT IS COMPOSED HERE RATHER THAN MOUNTED FROM THE DS, and why (each is a DS gap, recorded in the
// PR for graduation):
//   · the SECTION SHELL. The design mounts `ClinicalTermSection`, but its `title` is a string it
//     renders in `--color-text-muted` (no ink), its empty state is the fixed "No {title} on file."
//     (PROD-2734: copy must describe the record, and "unavailable" must not read as empty), and its
//     `+` is a plain button that cannot anchor a popover. The design passes a coloured element as
//     the title, which the DS then also writes into `aria-label` ("[object Object]"). So the shell
//     is the same anatomy (title · count · `+`, a `ul` of rows) built from DS parts.
//   · the PERSON CARD. The design mounts `PatientHeader variant="person-card"`, whose name is
//     14px / 700 / brand-depth (inline styles) and whose MRN rides the demographics run. The sheet
//     rules that the rule wins here (§1 Type, §1 Identity; §4b.5), so the card is DS `Avatar` + the
//     DS type roles: name `card-title`, the visit status as a DS `StatusChip` top-right, and under
//     the card the MRN (`meta` mono, text-secondary), once, last (not in a started encounter: the header has it).
import { useState, type ReactNode } from 'react';
import { Phone, Plus } from 'lucide-react';
import {
  Avatar,
  Button,
  ClinicalTermGroup,
  ClinicalTermRow,
  StatusChip,
  cn,
  type ClinicalTermAction,
  type ClinicalTermKind,
  type IpsEntry,
  type IpsSection,
  type PatientHeaderData,
} from '@scripsteam/scrips-signal-ds';
import { RailAnchoredSurface } from './pre-encounter-parts';
import {
  CHART_SECTION_ORDER,
  CHART_SECTION_WAYFIND,
  allergyDotLabel,
  allergyDotState,
  identityRun,
  realEntries,
  visitStatusChip,
  type AllergyDotState,
  type ChartWayfind,
} from './lib/chart-column-model';
import { V2_BODY_QUIET, V2_META } from './lib/design-v2';
import { V2_BODY, V2_CARD_TITLE, V2_META_STRONG } from '@/shared/ui/design-v2';
import { identityDob } from './lib/patient-identity';
import type { ChartColumnPerson, ChartColumnSection, ChartRailProps } from './lib/chart-rail-types';
// The rail's data types live in a neutral module (lib/chart-rail-types.ts) so the rail-v3 path does not import them from this legacy
// column; re-exported here for the legacy consumers.
export type { ChartColumnPerson, ChartColumnSection, ChartRailProps, ChartRowExtras, RailRowHandlers } from './lib/chart-rail-types';

/** The FHIR resource each card carries — the design's `data-block` eval hook. */
const DATA_BLOCK: Readonly<Record<string, string>> = {
  allergies: 'AllergyIntolerance',
  flags: 'Flag',
  'active-problems': 'Condition',
  medications: 'MedicationStatement',
  vaccinations: 'Immunization',
  procedures: 'Procedure',
  'family-history': 'FamilyMemberHistory',
  'social-history': 'SocialHistory',
  occupation: 'Occupation',
  education: 'Education',
};

const KIND: Readonly<Record<string, ClinicalTermKind>> = {
  allergies: 'allergy',
  'active-problems': 'dx',
  'resolved-problems': 'dx',
  procedures: 'procedure',
};

/** Actions that sit ON the row, in the ruled order ("+ Note / Resolve / More on point"); every
 *  other action is in "More". */
const ON_POINT: readonly string[] = ['+ Note', 'Resolve', 'Unresolve'];
/** The ruled word for putting a resolved problem back on the list (30 Sep: "Reactivate"). The
 *  handler is the rail's Unresolve, unchanged. */
const DISPLAY_LABEL: Readonly<Record<string, string>> = { Unresolve: 'Reactivate' };

/** The patient card: person first (photo or initials, name, age · sex) with the visit status, then
 *  the MRN under it, quiet and last; in a started encounter (`started`) no MRN row. */
export function ChartColumnPersonCard({ patient, demographics, importance, contacts, statusSlot, status, started, identityInHeader }: ChartColumnPerson) {
  const run = identityRun(demographics);
  // D-68 (design desk, 6 Oct): with the chart open the card IS the identity, so it carries the second
  // identifier too: the date of birth, D-45 ("DOB 15 Mar 1980"), beside the name, age and MRN.
  const dob = identityDob(demographics?.dob);
  const chip = statusSlot ? undefined : visitStatusChip(status);
  const language = [demographics?.language, demographics?.interpreterRequired ? 'Interpreter Required' : undefined]
    .filter(Boolean)
    .join(' - ');
  const vip = importance?.code?.toUpperCase() === 'VIP';
  // With the identity in the page header, a card with no language, importance or contacts would be an empty box: draw none.
  if (identityInHeader && !language && !importance && !(contacts && contacts.length > 0)) return null;
  return (
    <div
      data-slot="chart-person"
      className="flex flex-col"
    >
      <div role="region" aria-label="Patient" className="flex flex-col gap-2">
        <div className="flex items-start gap-3">
          {identityInHeader ? null : <Avatar src={patient.avatar} name={patient.name} size={48} importance={importance?.code} />}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {identityInHeader ? null : (
            <div className="flex items-start justify-between gap-2">
              <span data-slot="chart-person-name" className={cn(V2_CARD_TITLE, 'min-w-0 break-words')}>
                {patient.name}
              </span>
              {statusSlot ? <div className="shrink-0">{statusSlot}</div> : null}
              {chip ? (
                <StatusChip data-slot="chart-person-status" className="shrink-0" semantic={chip.semantic} tone={chip.tone}>
                  {chip.label}
                </StatusChip>
              ) : null}
            </div>
            )}
            {run && !identityInHeader ? (
              <span data-slot="chart-person-identity" className={V2_BODY}>
                {run}
              </span>
            ) : null}
            {dob && !identityInHeader ? (
              <span data-slot="chart-person-dob" className={V2_BODY}>
                DOB {dob}
              </span>
            ) : null}
            {language ? <span className={V2_BODY_QUIET}>{language}</span> : null}
            {/* VIP rides on the card's avatar; with the identity in the header there is no avatar here, so it shows as the chip. */}
            {importance && (!vip || identityInHeader) ? (
              <span className="flex">
                <StatusChip size="xs" semantic="neutral" tone="soft" title={importance.display}>
                  {importance.code}
                </StatusChip>
              </span>
            ) : null}
          </div>
        </div>
        {contacts && contacts.length > 0 ? (
          // With the identity in the page header the contacts open the card, so no rule above them.
          <ul
            aria-label="Contacts"
            className={cn('m-0 flex list-none flex-col gap-2 p-0', !identityInHeader && 'border-t border-border-card pt-2')}
          >
            {contacts.map((c) => {
              const relation = [c.relation, c.isProxy ? 'Proxy' : undefined].filter(Boolean).join(' · ');
              return (
                <li key={c.id} className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 flex-col">
                    <span className={cn(V2_BODY, 'truncate')}>{c.name}</span>
                    {relation ? <span className={V2_META}>{relation}</span> : null}
                  </span>
                  {c.phone ? (
                    <a
                      href={`tel:${c.phone}`}
                      aria-label={`Call ${c.name}`}
                      className="inline-flex shrink-0 text-text-secondary hover:text-text-primary"
                    >
                      <Phone className="size-4" aria-hidden />
                    </a>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      {!started && !identityInHeader && demographics?.mrn ? (
        // The row under the card: the MRN right (mono 400 11/16 text-secondary, never wraps). Off the card, as D-49(4)
        // rules. The started encounter (`started`) leaves it out: the MRN is the page header's there.
        <div data-slot="chart-person-visit-row" className="flex items-baseline justify-between gap-2 px-1">
          {demographics?.mrn ? (
            <span
              data-slot="chart-person-mrn"
              title="Medical record number"
              className={cn(V2_META, 'ml-auto shrink-0 whitespace-nowrap font-mono leading-meta')}
            >
              MRN {demographics.mrn}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The LAST row of a card hugs its content. The DS `ClinicalTermRow` reserves `min-h-8` (32px)
 * whenever the row has actions, for its action cluster, which on a hover device is absolutely
 * placed and transparent until hover. A one-line row is 16px of text, so the reserve drew an empty
 * 16px band under the last row, on top of the card's 12px padding (the Flags card, design desk
 * review of #574). Between rows the reserve stays: it is the design's 40px row rhythm.
 */
const LAST_ROW = 'min-h-0';

interface OpenRowSurface {
  sectionId: string;
  entryId: string;
  label: string;
  anchor: Element | null;
  seq: number;
}

/** A section of the left panel: the prototype's grammar (title with its count, `+`, rows, a hairline under it). */
function ChartSectionCard({
  model,
  title,
  wayfind,
  count,
  children,
}: {
  model: ChartColumnSection;
  title: string;
  wayfind: ChartWayfind;
  count: number;
  children: ReactNode;
}) {
  const { section } = model;
  const addLabel = model.addLabel ?? `Add to ${section.title}`;
  // Lucide's plus at the app's 2 px, in the quiet 24 control the left panel draws (design rules, icons; left-panel block).
  const addButton = (
    <Button data-slot="chart-section-add" variant="tertiary" size="sm" aria-label={addLabel} icon={<Plus aria-hidden />} />
  );
  const add = model.addPopover ? (
    <RailAnchoredSurface
      label={addLabel}
      trigger={addButton}
      render={model.addPopover}
      onOpenChange={model.onAddOpenChange}
    />
  ) : section.onAdd ? (
    <Button
      data-slot="chart-section-add"
      variant="tertiary"
      size="sm"
      aria-label={addLabel}
      icon={<Plus aria-hidden />}
      onClick={section.onAdd}
    />
  ) : null;
  return (
    <section
      aria-label={title}
      data-slot="chart-section"
      data-section={section.id}
      data-block={DATA_BLOCK[section.id]}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
          <span data-slot="chart-section-title" data-wayfind={wayfind} className={cn(V2_META_STRONG, 'truncate')}>
            {title}
          </span>
          {/* "Allergies · 0": the count rides the title, zero included, as in the prototype's section titles. */}
          <span data-slot="chart-section-count" className={V2_META_STRONG}>
            {count}
          </span>
        </div>
        {add}
      </div>
      {children}
    </section>
  );
}

/** The column's props: the neutral rail props (lib/chart-rail-types.ts), shared with the rail-v3 path. */
export type ChartColumnV2Props = ChartRailProps;

/** The sections the device-data card follows: the safety summary, ending with Flags. */
const DEVICE_DATA_AFTER: readonly string[] = ['allergies', 'active-problems', 'medications', 'flags'];

/** The started-encounter chart column (frame v2, switch ON only). */
export function ChartColumnV2({ person, sections, loading, loadError, loader, children, deviceData }: ChartColumnV2Props) {
  const [resolvedOpen, setResolvedOpen] = useState(false);
  const [openRow, setOpenRow] = useState<OpenRowSurface | null>(null);

  const byId = new Map(sections.map((m) => [m.section.id, m]));
  const ordered = [
    ...CHART_SECTION_ORDER.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : [])),
    ...sections.filter((m) => !CHART_SECTION_ORDER.includes(m.section.id)),
  ];
  const resolved = byId.get('resolved-problems');
  const active = byId.get('active-problems');
  // After the safety summary (allergies, problems, medications, flags): after the last of those present.
  const deviceDataAt = ordered.reduce((at, m, i) => (DEVICE_DATA_AFTER.includes(m.section.id) ? i + 1 : at), 0);

  /** Open a row action. With a surface: anchored to the pressed control — the event target for an
   *  on-point action; for an overflow item (the DS calls it with no event, BEFORE it closes the
   *  menu) the one "More" trigger whose menu is open, i.e. `aria-expanded="true"` — always a
   *  control inside the card that draws the row (resolved rows live in the problems card), never
   *  whatever happens to hold focus; otherwise that card. Without a surface: the action's own
   *  handler, unchanged. */
  function runAction(model: ChartColumnSection, entry: IpsEntry, action: { label: string; onClick: () => void }) {
    return (event?: { currentTarget?: EventTarget | null }) => {
      const render = model.actionPopover?.(entry.id, action.label);
      if (!render) {
        action.onClick();
        return;
      }
      const hostId = model.section.id === 'resolved-problems' && active ? 'active-problems' : model.section.id;
      const card = document.querySelector(
        `[data-slot="chart-column"] [data-slot="chart-section"][data-section="${hostId}"]`,
      );
      const pressed = event?.currentTarget ?? card?.querySelector('button[aria-haspopup="menu"][aria-expanded="true"]');
      const inCard = pressed instanceof Element && pressed.isConnected && !!card?.contains(pressed);
      model.actionOpenChange?.(entry.id, action.label)?.(true);
      setOpenRow((prev) => ({
        sectionId: model.section.id,
        entryId: entry.id,
        label: action.label,
        anchor: inCard ? (pressed as Element) : card,
        seq: (prev?.seq ?? 0) + 1,
      }));
    };
  }

  function rowProps(model: ChartColumnSection, entry: IpsEntry, state: 'active' | 'resolved') {
    const id = model.section.id;
    const extras = model.rowExtras?.(entry.id);
    const onPoint: ClinicalTermAction[] = [];
    const more: ClinicalTermAction[] = [];
    // Stable sort: the on-point actions take the ruled order, the rest keep the rail's own.
    const rank = (label: string) => (ON_POINT.includes(label) ? ON_POINT.indexOf(label) : ON_POINT.length);
    const sortedActions = [...(entry.actions ?? [])].sort((x, y) => rank(x.label) - rank(y.label));
    for (const a of sortedActions) {
      const action: ClinicalTermAction = {
        label: DISPLAY_LABEL[a.label] ?? a.label,
        onClick: runAction(model, entry, a),
        ...(a.tone ? { tone: a.tone } : {}),
      };
      (ON_POINT.includes(a.label) ? onPoint : more).push(action);
    }
    return {
      term: entry.primary,
      code: entry.code,
      secondary: entry.secondary,
      state,
      ...(KIND[id] ? { kind: KIND[id] } : {}),
      // Active problems show the whole name, wrapped, so year and code follow it (look only; same prop as the Dx suggestions).
      ...(id === 'active-problems' && state === 'active' ? { nameOverflow: 'wrap' as const } : {}),
      // No dot: every row of a list shares it, so it says nothing (design rules, status: a colour every row shares carries
      // no meaning). A stopped medicine already says "Stopped" in words.
      showDot: false,
      ...(extras?.onsetYear ? { onsetYear: extras.onsetYear } : {}),
      ...(state === 'resolved' && extras?.resolvedOn ? { resolvedOn: extras.resolvedOn } : {}),
      actions: onPoint,
      overflow: more,
    };
  }

  function rows(model: ChartColumnSection, state: 'active' | 'resolved') {
    const entries = realEntries(model.section);
    return entries.map((e, i) => (
      <ClinicalTermRow
        key={e.id}
        {...rowProps(model, e, state)}
        {...(i === entries.length - 1 ? { className: LAST_ROW } : {})}
      />
    ));
  }

  function card(model: ChartColumnSection) {
    const id = model.section.id;
    if (id === 'resolved-problems' && active) return null; // drawn inside the problems card
    const entries = realEntries(model.section);
    const group =
      id === 'active-problems' && resolved ? (
        realEntries(resolved.section).length > 0 ? (
          <ClinicalTermGroup
            label="Resolved"
            count={realEntries(resolved.section).length}
            open={resolvedOpen}
            onToggle={() => setResolvedOpen((v) => !v)}
          >
            {rows(resolved, 'resolved')}
          </ClinicalTermGroup>
        ) : null
      ) : null;
    // "Unavailable" is information, so it is said whatever else the card shows (PROD-2804); a
    // plain empty resolved list draws no drawer at all.
    const resolvedNotice =
      id === 'active-problems' && resolved?.unavailable ? (
        <p className={cn(V2_BODY_QUIET, 'm-0 mt-2')}>{resolved.emptyLabel}</p>
      ) : null;
    return (
      <ChartSectionCard
        key={id}
        model={model}
        title={model.section.title}
        wayfind={CHART_SECTION_WAYFIND[id] ?? 'neutral'}
        count={entries.length}
      >
        {entries.length > 0 ? (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">{rows(model, id === 'resolved-problems' ? 'resolved' : 'active')}</ul>
        ) : null}
        {/* A section read empty folds to its title line (Antonin, 1 Nov: 0 items, collapsed); "unavailable" is
            information, so it is always said. */}
        {model.unavailable ? (
          <p className={cn(V2_BODY_QUIET, 'm-0', entries.length > 0 && 'mt-2')}>{model.emptyLabel}</p>
        ) : null}
        {group}
        {resolvedNotice}
      </ChartSectionCard>
    );
  }

  const surfaceModel = openRow ? byId.get(openRow.sectionId) : undefined;
  const surfaceRender = openRow ? surfaceModel?.actionPopover?.(openRow.entryId, openRow.label) : undefined;

  return (
    <aside
      aria-label="Patient summary"
      data-region="chart"
      data-slot="chart-column"
      className="flex flex-col font-sans"
    >
      <ChartColumnPersonCard {...person} />
      <div className="flex flex-col">
        {loading ? (
          loader
        ) : loadError ? (
          loadError
        ) : (
          <div data-slot="chart-sections" className="flex flex-col">
            {ordered.slice(0, deviceDataAt).map(card)}
            {deviceData}
            {ordered.slice(deviceDataAt).map(card)}
          </div>
        )}
      </div>
      {openRow && surfaceRender ? (
        <RailAnchoredSurface
          key={openRow.seq}
          label={DISPLAY_LABEL[openRow.label] ?? openRow.label}
          anchor={openRow.anchor}
          render={surfaceRender}
          onOpenChange={(open) => {
            surfaceModel?.actionOpenChange?.(openRow.entryId, openRow.label)?.(open);
            if (!open) setOpenRow(null);
          }}
        />
      ) : null}
      {children}
    </aside>
  );
}

/** Never green: the summary carries no "no known allergies" assertion, only a list. An empty list
 *  describes the record (PROD-2734) and reads like the design's "not yet reviewed" ring. */
const DOT_CLASS: Readonly<Record<AllergyDotState, string>> = {
  recorded: 'border-status-error bg-status-error',
  'none-recorded': 'border-status-warning bg-transparent',
  unavailable: 'border-status-warning bg-transparent',
  failed: 'border-status-warning bg-transparent',
  loading: 'border-border-strong bg-transparent',
};

/** The folded left panel's marker: the allergy dot (D-41: it stays visible whenever the chart is folded). It rides the
 *  folded toggle (left-panel-fold.tsx); the 72px strip with the initials is gone, the page header carries the identity. */
export function ChartColumnStrip({
  allergies,
  loading,
  loadError,
  unavailable,
}: {
  /** Kept for the callers that pass it; the marker no longer draws the initials. */
  patient?: PatientHeaderData;
  allergies?: IpsSection;
  loading?: boolean;
  loadError?: boolean;
  unavailable?: boolean;
}) {
  const state = allergyDotState({ allergies, loading, loadError, unavailable });
  const label = allergyDotLabel(state, allergies);
  return (
    <span
      role="img"
      aria-label={label}
      data-slot="chart-strip-allergy-dot"
      data-state={state}
      className={cn('inline-block size-2 shrink-0 rounded-full border-2', DOT_CLASS[state])}
    />
  );
}
